// UsageDashboard.tsx - Comprehensive per-user usage analytics dashboard
import React, { useEffect, useState } from 'react';
import { UserUsageAnalytics } from '../../types/analytics';
import {
  fetchUserUsageAnalytics,
  formatNumber,
  formatDate,
  formatRelativeTime,
  formatDuration,
  getPercentageColor,
  getPercentageTextColor
} from '../../services/analytics';

interface UsageDashboardProps {
  onClose?: () => void;
}

const UsageDashboard: React.FC<UsageDashboardProps> = ({ onClose }) => {
  const [analytics, setAnalytics] = useState<UserUsageAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(30);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    loadAnalytics();
  }, [selectedPeriod]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadAnalytics(true); // Silent refresh
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, selectedPeriod]);

  const loadAnalytics = async (silent: boolean = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await fetchUserUsageAnalytics(selectedPeriod);
      setAnalytics(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-slate-800 rounded-lg p-8 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-slate-800 rounded-lg p-8 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 text-white rounded hover:bg-slate-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const { user, rateLimits, usage, dailyHistory, patterns, recentActivity } = analytics;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Usage Dashboard</h2>
            <div className="flex items-center gap-3">
              <p className="text-slate-400 text-sm">{user.email}</p>
              <span className="text-slate-500 text-xs">•</span>
              <p className="text-slate-500 text-xs">
                Updated {formatRelativeTime(lastUpdated.toISOString())}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-2 rounded border transition-colors ${
                autoRefresh
                  ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                  : 'bg-slate-700 border-slate-600 text-slate-400'
              }`}
              title={autoRefresh ? 'Auto-refresh enabled' : 'Auto-refresh disabled'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={() => loadAnalytics()}
              className="px-3 py-2 bg-slate-700 text-white rounded border border-slate-600 hover:bg-slate-600 transition-colors"
              title="Refresh now"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(Number(e.target.value))}
              className="px-3 py-2 bg-slate-700 text-white rounded border border-slate-600 focus:outline-none focus:border-emerald-500"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-700 rounded transition-colors"
              >
                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Tier & Account Info */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-slate-700/50 rounded-lg p-4">
              <p className="text-slate-400 text-sm mb-1">Current Tier</p>
              <p className="text-2xl font-bold capitalize">
                <span className={user.tier === 'alpha' ? 'text-purple-400' : 'text-emerald-400'}>
                  {user.tier}
                </span>
              </p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4">
              <p className="text-slate-400 text-sm mb-1">Member Since</p>
              <p className="text-lg font-semibold text-white">{formatDate(user.createdAt)}</p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4">
              <p className="text-slate-400 text-sm mb-1">Tier Since</p>
              <p className="text-lg font-semibold text-white">
                {user.tierStartedAt ? formatDate(user.tierStartedAt) : 'N/A'}
              </p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4">
              <p className="text-slate-400 text-sm mb-1">Avg Tokens/Msg</p>
              <p className="text-lg font-semibold text-white">
                {formatNumber(Math.round(usage.lifetime.avgTokensPerMessage))}
              </p>
            </div>
          </div>

          {/* Rate Limits */}
          <div className="bg-slate-700/30 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Rate Limits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Hourly Limit */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-300">Hourly Messages</span>
                  <span className={`font-semibold ${getPercentageTextColor(rateLimits.hourly.percentage)}`}>
                    {rateLimits.hourly.current} / {rateLimits.hourly.limit}
                  </span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${getPercentageColor(rateLimits.hourly.percentage)}`}
                    style={{ width: `${Math.min(rateLimits.hourly.percentage, 100)}%` }}
                  />
                </div>
                <p className="text-slate-400 text-sm mt-2">
                  Resets in {formatDuration(rateLimits.hourly.resetIn)}
                </p>
              </div>

              {/* Daily Limit */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-300">Daily Messages</span>
                  <span className={`font-semibold ${getPercentageTextColor(rateLimits.daily.percentage)}`}>
                    {rateLimits.daily.current} / {rateLimits.daily.limit}
                  </span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${getPercentageColor(rateLimits.daily.percentage)}`}
                    style={{ width: `${Math.min(rateLimits.daily.percentage, 100)}%` }}
                  />
                </div>
                <p className="text-slate-400 text-sm mt-2">
                  Resets at {new Date(rateLimits.daily.resetAt).toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>

          {/* Usage Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Today's Usage */}
            <div className="bg-slate-700/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Today's Usage</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Messages</span>
                  <span className="text-xl font-semibold text-emerald-400">
                    {formatNumber(usage.today.messages)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Tokens</span>
                  <span className="text-xl font-semibold text-blue-400">
                    {formatNumber(usage.today.tokens)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Conversations</span>
                  <span className="text-xl font-semibold text-purple-400">
                    {formatNumber(usage.today.conversations)}
                  </span>
                </div>
              </div>
            </div>

            {/* Lifetime Usage */}
            <div className="bg-slate-700/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Lifetime Usage</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Messages</span>
                  <span className="text-xl font-semibold text-emerald-400">
                    {formatNumber(usage.lifetime.messages)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Tokens</span>
                  <span className="text-xl font-semibold text-blue-400">
                    {formatNumber(usage.lifetime.tokens)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300">Conversations</span>
                  <span className="text-xl font-semibold text-purple-400">
                    {formatNumber(usage.lifetime.conversations)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Trends */}
          <div className="bg-slate-700/30 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Daily Usage Trends</h3>
            {dailyHistory.length > 0 ? (
              <div className="space-y-2">
                {dailyHistory.slice(-14).map((day) => {
                  const maxMessages = Math.max(...dailyHistory.map(d => d.messages), 1);
                  const messagePercentage = (day.messages / maxMessages) * 100;
                  
                  return (
                    <div key={day.date} className="flex items-center gap-3">
                      <span className="text-slate-400 text-sm w-24 flex-shrink-0">
                        {formatDate(day.date)}
                      </span>
                      <div className="flex-1 flex items-center gap-3">
                        <div className="flex-1 bg-slate-700 rounded-full h-6 overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 transition-all duration-300 flex items-center justify-end pr-2"
                            style={{ width: `${messagePercentage}%` }}
                          >
                            {day.messages > 0 && (
                              <span className="text-xs text-white font-semibold">
                                {day.messages}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-slate-400 text-sm w-20 text-right">
                          {formatNumber(day.tokens)} tokens
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No usage data available</p>
            )}
          </div>

          {/* Usage Patterns */}
          <div className="bg-slate-700/30 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Usage Patterns</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-1">Peak Hour</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {patterns.peakHour}:00
                </p>
              </div>
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-1">Avg Messages/Day</p>
                <p className="text-2xl font-bold text-blue-400">
                  {formatNumber(Math.round(patterns.avgMessagesPerDay))}
                </p>
              </div>
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-1">Avg Tokens/Day</p>
                <p className="text-2xl font-bold text-purple-400">
                  {formatNumber(Math.round(patterns.avgTokensPerDay))}
                </p>
              </div>
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-1">Active Hours</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {patterns.hourlyDistribution.filter(h => h.messages > 0).length}
                </p>
              </div>
            </div>

            {/* Hourly Distribution */}
            <div className="mt-4">
              <p className="text-slate-400 text-sm mb-3">Messages by Hour</p>
              <div className="flex items-end gap-1 h-32">
                {patterns.hourlyDistribution.map((hour) => {
                  const maxMessages = Math.max(...patterns.hourlyDistribution.map(h => h.messages), 1);
                  const height = (hour.messages / maxMessages) * 100;
                  
                  return (
                    <div key={hour.hour} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full bg-emerald-500 rounded-t transition-all duration-300 hover:bg-emerald-400 relative group"
                        style={{ height: `${height}%` }}
                      >
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-slate-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                          {hour.hour}:00 - {hour.messages} msgs
                        </div>
                      </div>
                      {hour.hour % 3 === 0 && (
                        <span className="text-xs text-slate-500 mt-1">{hour.hour}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-slate-700/30 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Recent Activity</h3>
            {recentActivity.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.map((activity) => (
                  <div
                    key={activity.conversationId}
                    className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="text-white font-medium">
                        {activity.title || 'Untitled Conversation'}
                      </p>
                      <p className="text-slate-400 text-sm mt-1">
                        {activity.messageCount} messages • {formatNumber(activity.tokenCount)} tokens
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-400 text-sm">
                        {formatRelativeTime(activity.lastActivity)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageDashboard;
